import React from 'react';
import { Plus, Trash, Image as ImageIcon, FileText, BookOpen, Clock } from '@phosphor-icons/react';
import EnhancedRichTextEditor from '../../EnhancedRichTextEditor';
import MediaPickerInput from '../../MediaPickerInput';

export default function ArticleModule({ metadata, onChange }) {
  const handleChange = (field, value) => {
    onChange({ ...metadata, [field]: value });
  };

  const addReference = () => {
    handleChange('references', [...(metadata.references || []), '']);
  };

  const updateReference = (index, value) => {
    const newRefs = [...(metadata.references || [])];
    newRefs[index] = value;
    handleChange('references', newRefs);
  };

  const removeReference = (index) => {
    const newRefs = (metadata.references || []).filter((_, i) => i !== index);
    handleChange('references', newRefs);
  };

  const addCitation = () => {
    handleChange('citations', [...(metadata.citations || []), { text: '', source: '' }]);
  };

  const updateCitation = (index, field, value) => {
    const newCitations = [...(metadata.citations || [])];
    newCitations[index] = { ...newCitations[index], [field]: value };
    handleChange('citations', newCitations);
  };

  const removeCitation = (index) => {
    const newCitations = (metadata.citations || []).filter((_, i) => i !== index);
    handleChange('citations', newCitations);
  };

  return (
    <div className="space-y-6">
      {/* Hero Image */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <ImageIcon size={16} />
          Hero Image
        </label>
        <MediaPickerInput
          value={metadata.hero_image || ''}
          onChange={(url) => handleChange('hero_image', url)}
          accept="image/*"
          label="Select hero image"
          helpText="Recommended: 1600x900px for article header"
        />
      </div>

      {/* Excerpt */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <FileText size={16} />
          Excerpt / Summary
        </label>
        <textarea
          value={metadata.excerpt || ''}
          onChange={(e) => handleChange('excerpt', e.target.value)}
          placeholder="Brief summary that appears in listings and search results..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={3}
          maxLength={300}
        />
        <p className="text-xs text-muted-foreground mt-1">
          {(metadata.excerpt || '').length}/300 characters
        </p>
      </div>

      {/* Article Content */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <BookOpen size={16} />
          Article Content <span className="text-red-500">*</span>
        </label>
        <EnhancedRichTextEditor
          value={metadata.article_content || ''}
          onChange={(html) => handleChange('article_content', html)}
          placeholder="Write your article here..."
        />
      </div>

      {/* Reading Time */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Clock size={16} />
          Estimated Reading Time (minutes)
        </label>
        <input
          type="number"
          value={metadata.reading_time || ''}
          onChange={(e) => handleChange('reading_time', parseInt(e.target.value) || 0)}
          placeholder="e.g., 5"
          min="1"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Leave empty to auto-calculate based on word count
        </p>
      </div>

      {/* References */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <BookOpen size={16} />
            References & Sources
          </label>
          <button
            onClick={addReference}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-muted hover:bg-muted/80"
          >
            <Plus size={14} weight="bold" />
            Add Reference
          </button>
        </div>

        {(metadata.references || []).length === 0 && (
          <p className="text-sm text-muted-foreground italic">No references added</p>
        )}

        <div className="space-y-2">
          {(metadata.references || []).map((ref, index) => (
            <div key={index} className="flex gap-2 items-center group">
              <span className="text-xs text-muted-foreground font-mono">[{index + 1}]</span>
              <input
                type="text"
                value={ref}
                onChange={(e) => updateReference(index, e.target.value)}
                placeholder="Reference URL or description"
                className="flex-1 px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm"
              />
              <button
                onClick={() => removeReference(index)}
                className="p-2 rounded-lg text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Citations */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <Quotes size={16} />
            Citations
          </label>
          <button
            onClick={addCitation}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-muted hover:bg-muted/80"
          >
            <Plus size={14} weight="bold" />
            Add Citation
          </button>
        </div>

        {(metadata.citations || []).length === 0 && (
          <p className="text-sm text-muted-foreground italic">No citations added</p>
        )}

        <div className="space-y-3">
          {(metadata.citations || []).map((citation, index) => (
            <div key={index} className="p-4 bg-muted/50 rounded-lg group relative">
              <button
                onClick={() => removeCitation(index)}
                className="absolute top-2 right-2 p-1 rounded text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
              >
                <Trash size={14} />
              </button>
              <div className="space-y-2">
                <textarea
                  value={citation.text || ''}
                  onChange={(e) => updateCitation(index, 'text', e.target.value)}
                  placeholder="Citation text..."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm resize-none"
                  rows={2}
                />
                <input
                  type="text"
                  value={citation.source || ''}
                  onChange={(e) => updateCitation(index, 'source', e.target.value)}
                  placeholder="Source (author, publication, date)"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Add missing import
import { Quotes } from '@phosphor-icons/react';
